import { create } from 'zustand/index';

import { GetPrettyNumbersParams } from '@/api/types';
import { defaultFilterVals, defaultSearchParams } from '@/store/constants.ts';
import {
    CategoriesWithStatuses,
    DeviceTypesWithStatuses,
    DropdownOption,
    DropdownOptions,
    OperatorsWithStatuses,
} from '@/types';
import { getSearchParams, getTotalActiveFilters } from '@/utils';

export interface FilterState {
    currentPage: number;
    setCurrentPage: (currentPage: this['currentPage']) => void;

    prevPage: number;
    setPrevPage: (prevPage: this['prevPage']) => void;

    isFiltersOpen: boolean;
    setIsFiltersOpen: (open: this['isFiltersOpen']) => void;

    totalActiveFilters: number;

    searchParams: Partial<GetPrettyNumbersParams['search'] & { device: number[] }>;
    setSearchParams: (searchParams: this['searchParams']) => void;
    clearSearchParams: () => void;

    prefix: DropdownOption<number | null>;
    setPrefix: (prefix: this['prefix']) => void;
    clearPrefix: VoidFunction;

    digits: string[];
    setDigits: (digits: this['digits']) => void;

    regionsVal: DropdownOptions<number>;
    setRegionsVal: (regionsVal: this['regionsVal']) => void;
    clearRegionsVal: VoidFunction;

    tariffsVal: DropdownOptions<string>;
    setTariffsVal: (tariffsVal: this['tariffsVal']) => void;
    clearTariffsVal: VoidFunction;

    favNum: string;
    setFavNum: (favNum: this['favNum']) => void;
    clearFavNum: VoidFunction;

    priceMin: string;
    setPriceMin: (priceMin: this['priceMin']) => void;

    priceMax: string;
    setPriceMax: (priceMax: this['priceMax']) => void;

    categories: CategoriesWithStatuses;
    setCategories: (categories: this['categories']) => void;
    clearCategories: VoidFunction;

    operators: OperatorsWithStatuses;
    setOperators: (operators: this['operators']) => void;
    clearOperators: VoidFunction;

    deviceTypes: DeviceTypesWithStatuses;
    setDeviceTypes: (deviceTypes: this['deviceTypes']) => void;
    clearDeviceTypes: VoidFunction;

    searchParamsStr: string;
    setSearchParamsStr: () => void;

    prevSearchParamsStr: string;
    setPrevSearchParamsStr: () => void;

    clearSearchParamsStr: () => void;
    clearAllFilters: () => void;
    clearPriceRange: () => void;
}

const {
    prefix,
    digits,
    regionsVal,
    tariffsVal,
    favNum,
    priceMax,
    priceMin,
    operators,
    categories,
    deviceTypes,
} = defaultFilterVals;

export const useFilterStore = create<FilterState>()(set => ({
    currentPage: 0,
    setCurrentPage: currentPage => set(() => ({ currentPage })),

    prevPage: 0,
    setPrevPage: prevPage => set(() => ({ prevPage })),

    isFiltersOpen: false,
    setIsFiltersOpen: isFiltersOpen => set(() => ({ isFiltersOpen })),

    totalActiveFilters: 0,

    searchParams: defaultSearchParams,
    setSearchParams: newSearchParams =>
        set(({ searchParams }) => ({ searchParams: { ...searchParams, ...newSearchParams } })),
    clearSearchParams: () => set(() => ({ searchParams: defaultSearchParams })),

    categories,
    setCategories: categories =>
        set(({ setSearchParams }) => {
            if (categories.every(category => !category.active)) {
                setSearchParams({ categories: [] });
            }

            return { categories };
        }),
    clearCategories: () =>
        set(({ setSearchParams, setSearchParamsStr, categories: currentCategories }) => {
            const clearedCategories = currentCategories.map(category => ({
                ...category,
                active: false,
            }));
            setSearchParams({ categories: [] });
            setSearchParamsStr();

            return { categories: clearedCategories };
        }),

    operators,
    setOperators: operators =>
        set(({ setSearchParams }) => {
            if (operators.every(operator => !operator.active)) {
                setSearchParams({ operators: [] });
            }

            return { operators };
        }),
    clearOperators: () =>
        set(({ setSearchParams, setSearchParamsStr, operators: currentOperators }) => {
            const clearedOperators = currentOperators.map(operator => ({
                ...operator,
                active: false,
            }));
            setSearchParams({ operators: [] });
            setSearchParamsStr();

            return { operators: clearedOperators };
        }),

    deviceTypes,
    setDeviceTypes: deviceTypes =>
        set(({ setSearchParams }) => {
            if (deviceTypes.every(deviceType => !deviceType.active)) {
                setSearchParams({ device: [] });
            }

            return { deviceTypes };
        }),
    clearDeviceTypes: () =>
        set(({ setSearchParams, setSearchParamsStr, deviceTypes: currentDeviceTypes }) => {
            const clearedDeviceTypes = currentDeviceTypes.map(deviceType => ({
                ...deviceType,
                active: false,
            }));
            setSearchParams({ device: [] });
            setSearchParamsStr();

            return { deviceTypes: clearedDeviceTypes };
        }),

    searchParamsStr: '',
    setSearchParamsStr: () =>
        set(({ searchParams }) => {
            const { prefix, msisdnMask, isFree, ...rest } = searchParams;

            return {
                totalActiveFilters: getTotalActiveFilters(rest),
                searchParamsStr: getSearchParams(
                    searchParams as GetPrettyNumbersParams['search'] & { device: number[] }
                ),
            };
        }),

    prevSearchParamsStr: '',
    setPrevSearchParamsStr: () =>
        set(({ searchParamsStr }) => ({ prevSearchParamsStr: searchParamsStr })),

    prefix,
    setPrefix: prefix => set(() => ({ prefix })),
    clearPrefix: () =>
        set(({ setSearchParams, setSearchParamsStr }) => {
            setSearchParams({ prefix: undefined });
            setSearchParamsStr();

            return { prefix };
        }),

    digits,
    setDigits: digits => set(() => ({ digits })),

    favNum,
    setFavNum: favNum => set(() => ({ favNum })),
    clearFavNum: () =>
        set(({ setSearchParams, setSearchParamsStr }) => {
            setSearchParams({ msisdnMask: undefined, isFree: false });
            setSearchParamsStr();

            return { favNum };
        }),

    regionsVal,
    setRegionsVal: regionsVal => set(() => ({ regionsVal })),
    clearRegionsVal: () =>
        set(({ setSearchParams, setSearchParamsStr }) => {
            setSearchParams({ regions: [] });
            setSearchParamsStr();

            return { regionsVal };
        }),

    tariffsVal,
    setTariffsVal: tariffsVal => set(() => ({ tariffsVal })),
    clearTariffsVal: () =>
        set(({ setSearchParams, setSearchParamsStr }) => {
            setSearchParams({ tariffs: [] });
            setSearchParamsStr();

            return { tariffsVal };
        }),

    priceMin,
    setPriceMin: priceMin => set(() => ({ priceMin })),

    priceMax,
    setPriceMax: priceMax => set(() => ({ priceMax })),

    clearPriceRange: () =>
        set(({ setSearchParams, setSearchParamsStr }) => {
            setSearchParams({ priceMin: undefined, priceMax: undefined });
            setSearchParamsStr();

            return { priceMin, priceMax };
        }),

    clearAllFilters: () =>
        set(({ clearSearchParams, categories, operators, deviceTypes }) => {
            const newCategories = categories.map(category => ({ ...category, active: false }));
            const newOperators = operators.map(operator => ({ ...operator, active: false }));
            const newDeviceTypes = deviceTypes.map(deviceType => ({
                ...deviceType,
                active: false,
            }));

            clearSearchParams();
            return {
                prefix,
                digits,
                favNum,
                categories: newCategories,
                operators: newOperators,
                regionsVal,
                tariffsVal,
                priceMin,
                priceMax,
                deviceTypes: newDeviceTypes,
            };
        }),

    clearSearchParamsStr: () =>
        set(() => ({ searchParamsStr: '', prevSearchParamsStr: '', totalActiveFilters: 0 })),
}));
